import { useEffect, useRef } from 'react';
import { Heart, X } from 'lucide-react';

/**
 * "Beneficios de los ejercicios", en las dos formas en que aparece.
 *
 * - Al entrar en `/ejercicios`, en un modal CENTRADO: es lo primero que ve el
 *   estudiante y es lo que le dice para qué sirve lo que tiene delante antes de
 *   empezar.
 * - Al final de la misma página, como estaba: para el que ya lo leyó y lo
 *   quiere tener a mano sin volver a cerrarlo.
 *
 * El texto vive en UN solo sitio (`BENEFICIOS`) porque duplicarlo era la forma
 * de que la tarjeta y el modal dejaran de decir lo mismo en la siguiente
 * edición de la lista.
 *
 * Modal a mano (`role="dialog"`, Escape, scroll bloqueado) como
 * `JuegoBloqueadoModal`: Radix está en dependencias pero no se usa en ninguna
 * página.
 */

export const BENEFICIOS: readonly string[] = [
  'Reducen el estrés y la ansiedad',
  'Mejoran la concentración y el enfoque',
  'Promueven un mejor sueño',
  'Aumentan la energía y la vitalidad',
];

interface ModalBeneficiosProps {
  open: boolean;
  onClose: () => void;
}

export function ModalBeneficios({ open, onClose }: ModalBeneficiosProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    // El foco entra en el panel: si no, el tabulador sigue recorriendo los
    // botones de la lista que están detrás del overlay.
    panelRef.current?.focus();

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    // `items-center` en móvil también: una hoja inferior choca con el menú
    // fijo del layout (`fixed bottom-0`), que tapa la parte baja del panel.
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <button
        type="button"
        aria-label="Cerrar beneficios"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default"
        tabIndex={-1}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-beneficios"
        tabIndex={-1}
        className="relative w-full max-w-sm rounded-card border border-v-outline bg-card p-6 shadow-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <span
            aria-hidden
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-v-violeta-100"
          >
            <Heart className="h-6 w-6 text-v-violeta-700" />
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-v-on-surface-variant transition-colors active:bg-black/5"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <h2 id="titulo-beneficios" className="mt-3 text-xl font-bold text-v-on-surface">
          Beneficios de los ejercicios
        </h2>

        <ul className="mt-3 space-y-2">
          {BENEFICIOS.map((beneficio) => (
            <li key={beneficio} className="flex items-start gap-2.5">
              <Heart
                className="mt-0.5 h-4 w-4 shrink-0 text-v-violeta-500"
                aria-hidden="true"
              />
              <span className="text-v-on-surface-variant">{beneficio}</span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={onClose}
          className="mt-6 min-h-12 w-full rounded-pill bg-primary px-6 text-sm font-semibold text-primary-foreground"
        >
          Entendido
        </button>
      </div>
    </div>
  );
}

/** La tarjeta del final de la página, tal cual estaba. */
export function BeneficiosEjercicios() {
  return (
    <div className="bg-gradient-to-r from-v-violeta-100 to-v-rosa-100 rounded-xl p-6 border border-v-violeta-100">
      <div className="flex items-start space-x-4">
        <div className="bg-v-violeta-100 rounded-full p-3">
          <Heart className="h-6 w-6 text-v-violeta-700" />
        </div>
        <div>
          <h3 className="font-semibold text-v-violeta-700 mb-2">Beneficios de los Ejercicios</h3>
          <ul className="text-sm text-v-violeta-700 space-y-1">
            {BENEFICIOS.map((beneficio) => (
              <li key={beneficio}>• {beneficio}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}